import { useRef } from "react";
import styles from "../list.module.css";
import dayjs from "dayjs";
import { Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, Checkbox } from "@mui/material";
import { Inbox, Map, X } from "react-feather";
import { AddOutlined, FileDownloadOutlined, PersonAddAltOutlined, PrintOutlined } from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import TablePaginationComponent from "../../../../components/TablePagination";
import { StyledMainTableCell, StyledMainTableRow } from "../../../../components/TableComponent";
import { authData } from "../../../../components/GetAuth";
import { ComponentToPrintList } from "../ComponentToPrintList";
import ServiceTag from "../../../../components/ServiceTag";
import ListToolbar from "../../../../components/ListToolbar/ListToolbar";
import { downloadCsv } from "../../../../components/ListToolbar/csv";
import { EXPORT_COLUMNS } from "../ListView";

// Map view, step 1: tick bookings, choose drivers, then plan the routes
const Step1 = ({ selected, driverList, selectedDriverList, filteredData, filterVars, setFilterVars, setFormOpen, setSelectedDriverList, getOptimizeRoute, selectRowEvent, setSelected, page, setPage, rowsPerPage, setRowsPerPage, totalCount, viewSwitch, onAddDriver }) => {
    const auth = authData();
    const canAssign = auth?.user_type === 2 || auth?.permissions?.indexOf('bookings:assign-driver') > -1;
    const printRef = useRef();
    const handlePrint = useReactToPrint({ content: () => printRef.current });
    const driverCount = selectedDriverList?.length || 0;

    const paginationChangePage = (event, newPage) => {
        setPage(newPage);
    }

    const paginationChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    return (<>
        {selectedDriverList?.length > 0 && <div>
            <p className={`${styles.DriverTitle}`}>Selected driver{selectedDriverList?.length > 1 ? "s" : ""} <span className={styles.Mono}>({selectedDriverList?.length})</span></p>
            <div className={`${styles.DriverCardSec}`}>
                {driverList?.map(item => {
                    return (selectedDriverList?.indexOf(item?.id) > -1) ? <div className={`${styles.DriverCard}`} key={item?.id}>
                        <div className={`${styles.DriverCardUnder}`}>
                            <div className={`${styles.DriverCardHead}`}>
                                <p>{item?.name}</p>
                                <button aria-label={`Remove ${item?.name}`} onClick={(e) => {
                                    setSelectedDriverList((prev) => {
                                        return prev?.filter((i) => i !== item?.id);
                                    });
                                }}>
                                    <X size={16} />
                                </button>
                            </div>
                            <p className={`${styles.DriverCardAddress}`}>{item?.address}</p>
                        </div>
                    </div> : null;
                })}
            </div>
        </div>}


        <ListToolbar
            search={filterVars?.searchKey}
            onSearch={(value) => setFilterVars((prev) => ({ ...prev, searchKey: value }))}
            dateRange={filterVars?.date}
            onDateRange={(value) => setFilterVars((prev) => ({ ...prev, date: value }))}
            viewSwitch={viewSwitch}
            selectedCount={selected.length}
            onClearSelection={() => setSelected([])}
            secondaryAction={canAssign ? { label: driverCount ? `Choose drivers (${driverCount})` : "Choose drivers", icon: <PersonAddAltOutlined />, onClick: () => setFormOpen(true) } : null}
            primaryAction={canAssign ? {
                label: selected.length ? `Plan route (${selected.length})` : "Plan route",
                icon: <Map size={16} />,
                onClick: () => getOptimizeRoute(),
                disabled: selected.length === 0 || driverCount === 0,
                hint: selected.length === 0 ? "Tick the bookings to route first" : "Choose at least one driver first",
            } : null}
            menuItems={[
                canAssign && onAddDriver && { label: "Add driver", icon: <AddOutlined fontSize="small" />, onClick: onAddDriver },
                { label: "Export CSV", icon: <FileDownloadOutlined fontSize="small" />, onClick: () => downloadCsv("assign-bookings.csv", EXPORT_COLUMNS, filteredData), disabled: !filteredData?.length },
                { label: "Print bookings", icon: <PrintOutlined fontSize="small" />, onClick: handlePrint, disabled: !filteredData?.length },
            ]}
        />
        <div style={{ display: "none" }}>
            <ComponentToPrintList ref={printRef} data={filteredData} />
        </div>
        <div className={`${styles.CusTable}`}>
            <TableContainer component={Paper} className={styles.ResponsiveTableWrap}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {canAssign && <StyledMainTableCell padding="checkbox">
                                <Checkbox
                                    className={`${styles.AllCheckbox}`}
                                    color="primary"
                                    indeterminate={selected?.length > 0 && selected?.length < filteredData?.length}
                                    checked={filteredData?.length > 0 && selected?.length === filteredData?.length}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const newSelected = filteredData.map((n) => n.id);
                                            setSelected(newSelected);
                                            return;
                                        }
                                        setSelected([]);
                                    }}
                                />
                            </StyledMainTableCell>}
                            <StyledMainTableCell>Booking Id</StyledMainTableCell>
                            <StyledMainTableCell>Post Code</StyledMainTableCell>
                            <StyledMainTableCell>Customer Name</StyledMainTableCell>
                            <StyledMainTableCell>Address</StyledMainTableCell>
                            <StyledMainTableCell>Order Status</StyledMainTableCell>
                            <StyledMainTableCell>Contact No</StyledMainTableCell>
                            <StyledMainTableCell>Date</StyledMainTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredData.map((item) => {
                            const isItemSelected = selected.indexOf(item?.id) !== -1;

                            return (<StyledMainTableRow key={item?.id} className={styles.ResponsiveRow} sx={{ "&:last-child td, &:last-child th": { border: 0, cursor: "pointer" } }} onClick={(e) => selectRowEvent(item?.id)} aria-checked={isItemSelected} selected={isItemSelected}>
                                {canAssign && <TableCell padding="checkbox" className={styles.CheckboxCell}>
                                    <Checkbox color="primary" checked={isItemSelected} />
                                </TableCell>}
                                <TableCell component="th" scope="row" data-label="Booking Id" className={styles.Mono}>{item?.booking_id}<ServiceTag booking={item} /></TableCell>
                                <TableCell component="th" scope="row" data-label="Post Code">{item?.post_code}</TableCell>
                                <TableCell component="th" scope="row" data-label="Customer">{item?.customer_name}</TableCell>
                                <TableCell component="th" scope="row" data-label="Address">{item?.pickup_details?.address}</TableCell>
                                <TableCell data-label="Status">{item?.payment_status === 1 || item?.payment_status === "1" ? (<Button className={`${styles.StatusBU} ${styles.DisaBU}`}>Paid</Button>) : (<Button className={`${styles.StatusBU}`}>Unpaid</Button>)}</TableCell>
                                <TableCell component="th" scope="row" data-label="Contact No">{item?.pickup_details?.phone}</TableCell>
                                <TableCell component="th" scope="row" data-label="Date">{dayjs(item?.pickup_date).format("DD/MM/YYYY")}</TableCell>
                            </StyledMainTableRow>);
                        })}

                        {filteredData.length === 0 && (<TableRow>
                            <TableCell colSpan={10} align="center">
                                <div className={styles.EmptyState}>
                                    <Inbox size={32} />
                                    <p className={styles.EmptyStateTitle}>No bookings match this view</p>
                                    <p className={styles.EmptyStateText}>Try a different date range or clear your search.</p>
                                </div>
                            </TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredData?.length > 0 && (<div className={styles.PaginationWrap}>
                <TablePaginationComponent
                    count={totalCount}
                    colSpan={6}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={paginationChangePage}
                    handleChangeRowsPerPage={paginationChangeRowsPerPage}
                />
            </div>)}
        </div>

        {/* Mobile-only: keeps the primary "build a route" action reachable
            without scrolling back to the top of a long booking list. */}
        {
            canAssign && selected.length > 0 && (
                <div className={styles.MobileStickyBar}>
                    <div className={styles.MobileStickyBarInner}>
                        <span className={styles.MobileStickyBarCount}>{selected.length} selected</span>
                        <button
                            type="button"
                            className={styles.MobileStickyBarBU}
                            disabled={selectedDriverList?.length === 0}
                            onClick={(e) => getOptimizeRoute()}
                        >
                            <Map size={16} /> Build route
                        </button>
                    </div>
                </div>
            )
        }
    </>);
};

export default Step1;